package com.steamstreet.vegasful.browser.account.intelligence

import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.account.GraphQL
import com.steamstreet.vegasful.browser.account.loginPanel
import com.steamstreet.vegasful.browser.components.autoGrow
import com.steamstreet.vegasful.browser.components.typewriterPlaceholders
import com.steamstreet.vegasful.css.VegasfulStyles
import com.steamstreet.vegasful.css.css
import com.steamstreet.vegasful.graphql.api.ConversationInput
import com.steamstreet.vegasful.graphql.api.GPSCoordinatesInput
import com.steamstreet.vegasful.web.ClientController
import com.steamstreet.vegasful.web.analytics.Analytics
import com.steamstreet.vegasful.web.analytics.AnalyticsEvent
import com.steamstreet.vegasful.web.analytics.AnalyticsParam
import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.css.*
import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.js.onClickFunction
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLTextAreaElement
import kotlin.coroutines.resume
import kotlin.js.Promise
import kotlin.js.json

private const val intelligenceInputId: String = "intelligence-input"
private const val threadId: String = "intelligence-thread"
private const val composerId: String = "intelligence-composer"

/**
 * How long a single turn is polled before the browser gives up on it.
 *
 * A conversation runs in the server process rather than on a durable queue, so a task replaced
 * mid-answer -- which any deploy does -- leaves its record RUNNING until the record expires. The
 * poll has to end itself, or the page sits asking about a dead turn once a second indefinitely.
 */
private const val maxPollAttempts: Int = 180

/** The `context` every failure from this feature is reported under. */
private const val INTELLIGENCE_CONTEXT: String = "intelligence"


/**
 * A list of predefined input hints or suggestions for the user when interacting with the intelligence system.
 * These hints provide examples of queries users can ask, such as event recommendations or venue suggestions.
 * The list supports dynamic placeholder functionality in the text area input within the intelligence interface.
 */
private val inputHints = listOf(
    "What’s happening tonight?",
    "Any rooftop shows this weekend?",
    "Find me something low-key and fun.",
    "Where’s a good comedy show?",
    "Show me what’s electric in Vegas tonight."
)


/**
 * The `Intelligence` class handles the main interaction and behavior
 * of the Vegasful Intelligence feature. It provides a dynamic UI for
 * logged-in and logged-out users and facilitates backend queries related
 * to user input.
 *
 * @property element The root DOM element where the Intelligence feature will be rendered.
 * @property scope The coroutine scope for managing asynchronous operations related to the class tasks.
 *
 * Implements the `ClientController` interface to define a `run` method for initializing the UI and handling user interactions.
 */
class Intelligence(private val element: Element, private val scope: CoroutineScope) : ClientController {
    private val httpClient = HttpClient()

    /** The thread being added to, once the first question has opened one. */
    private var conversationId: String? = null

    /** How many questions have been asked, used to give each turn its own status element. */
    private var turns: Int = 0

    override suspend fun run() {
        element.append {
            if (!Account.isLoggedIn()) {
                div {
                    h1 {
                        css {
                            fontSize = (1.75).rem
                            marginBottom = (1).rem
                        }
                        +"Login"
                    }
                    div {
                        css {
                            fontWeight = FontWeight.w300
                            marginBottom = (2).rem
                        }
                        +"To use Vegasful Intelligence, you must have a Vegasful account"
                    }
                    div {
                        css {
                            maxWidth = 484.px
                            margin = Margin(0.px, LinearDimension.auto)
                        }
                        loginPanel()
                    }
                }
            } else {
                // The thread accumulates one block per turn; the composer sits below it and is
                // reused, so asking a follow-up is the same gesture as asking the first question.
                div {
                    id = threadId
                }
                div {
                    id = composerId
                    textArea {
                        this.id = intelligenceInputId
                        placeholder =
                            "Ask anything about what you'd like to do... (e.g. 'Are there any jazz or soul events coming up?')"
                        css {
                            width = 100.pct
                            height = 200.px
                            padding = Padding(10.px)
                            borderRadius = 10.px
                        }
                    }

                    intelligenceButton("Ask Vegasful") {
                        ask()
                    }
                }
            }
        }

        val textInput = document.getElementById(intelligenceInputId) as? HTMLTextAreaElement
        textInput?.autoGrow(2)
        textInput?.typewriterPlaceholders(scope, inputHints)

        // A question asked from the home page arrives held rather than typed. It is asked only
        // once the visitor is signed in; until then it waits behind the sign-in panel above.
        if (Account.isLoggedIn()) {
            ConciergeHandoff.take()?.let { held ->
                scope.launch { executeQuery(held) }
            }
        }
    }

    /**
     * Take whatever is in the box and ask it.
     */
    private fun ask() {
        val input = document.getElementById(intelligenceInputId) as? HTMLTextAreaElement
        val query = input?.value?.trim()
        if (query.isNullOrEmpty()) return

        input.value = ""
        scope.launch {
            executeQuery(query)
        }
    }

    /**
     * Show or hide the composer. It is hidden while a turn runs so a second question cannot be
     * asked before the first has an answer to be a follow-up to.
     */
    private fun composerVisible(visible: Boolean) {
        (document.getElementById(composerId) as? HTMLElement)?.hidden = !visible
    }


    private suspend fun executeQuery(query: String) {
        // The question itself never leaves the browser: only how long it was. Intelligence
        // questions are free text about what somebody wants to do on a given night, which is
        // exactly the sort of thing `docs/analytics-plan.md` keeps out of the analytics stream.
        Analytics.track(
            AnalyticsEvent.INTELLIGENCE_QUERIED,
            AnalyticsParam.QUERY_LENGTH to query.length
        )

        val thread = document.getElementById(threadId) ?: return
        val statusElementId = "intelligence-status-$turns"
        turns += 1

        // The question and its answer are appended, never replacing what came before: the thread
        // above is the context the follow-up is asked against, so the user has to be able to see
        // it while they type the next question.
        thread.append {
            div {
                css {
                    marginBottom = 32.px
                }
                p {
                    css {
                        fontWeight = FontWeight.w600
                        fontSize = 18.px
                        textAlign = TextAlign.left
                    }
                    +query
                }
                div {
                    id = statusElementId
                    +"Analyzing Vegas events..."
                }
            }
        }

        composerVisible(false)

        try {
            console.log("Running query: $query")
            val location = grantedLocation()
            val id = GraphQL.client {
                conversations {
                    start(ConversationInput(query, location, conversationId)) {
                        id
                    }
                }
            }.conversations.start.id

            // Every later question in this thread is sent against the same id, which is what
            // lets the server read "something earlier" as a refinement rather than a fresh
            // search.
            conversationId = id

            // Poll for conversation results using simple HTTP polling
            pollForConversation(id, statusElementId)
        } catch (e: Exception) {
            // Polling handles its own failures; this is the conversation failing to start at
            // all. Without it the thrown exception would escape into the launching scope and
            // leave the composer hidden, with no way to ask anything again.
            console.error("Unable to start the conversation:", e)
            document.getElementById(statusElementId)?.innerHTML =
                "<p style='color: red; padding: 20px; text-align: center;'>We couldn't start that request. Please try again.</p>"
            Analytics.apiError(INTELLIGENCE_CONTEXT, "conversation start failed")
            reportResponded(success = false)
        } finally {
            // The composer always comes back, so the next question is visibly asked against
            // everything above it. Its placeholder is left to the typewriter, which owns that
            // property and rewrites it continuously.
            composerVisible(true)
        }
    }

    /**
     * The user's coordinates, but only when the browser has *already* granted permission.
     *
     * This deliberately never prompts. Whether the concierge should ask for location, and what it
     * offers in return, is a product decision -- and a permission dialog appearing the first time
     * somebody types a question would quietly make that decision for us. Until it is made, this
     * uses a permission that already exists and sends nothing when there is none.
     */
    private suspend fun grantedLocation(): GPSCoordinatesInput? {
        return try {
            val permissions = window.navigator.asDynamic().permissions ?: return null
            val status = (permissions.query(json("name" to "geolocation")) as Promise<dynamic>).await()
            if (status.state != "granted") return null

            suspendCancellableCoroutine { continuation ->
                window.navigator.asDynamic().geolocation.getCurrentPosition(
                    { position: dynamic ->
                        continuation.resume(
                            GPSCoordinatesInput(
                                (position.coords.latitude as Number).toFloat(),
                                (position.coords.longitude as Number).toFloat()
                            )
                        )
                    },
                    { _: dynamic ->
                        continuation.resume(null)
                    }
                )
            }
        } catch (e: Throwable) {
            // Location is an enhancement. A browser without the permissions API, or one that
            // rejects the query, should still get an answer to the question that was asked.
            console.log("Location unavailable, continuing without it")
            null
        }
    }

    private suspend fun pollForConversation(id: String, statusElementId: String) {
        val statusElement = document.getElementById(statusElementId) ?: return
        var attempts = 0

        try {
            // Poll the conversation endpoint until complete
            while (true) {
                delay(1000) // Poll every second

                attempts += 1
                if (attempts > maxPollAttempts) {
                    statusElement.innerHTML = "<p style='color: red; padding: 20px; text-align: center;'>This is taking longer than expected. Please try asking again.</p>"
                    Analytics.apiError(INTELLIGENCE_CONTEXT, "conversation timed out")
                    reportResponded(success = false)
                    break
                }

                // Fetch HTML from the server using Ktor
                val response = httpClient.get("/conversations/$id")

                // Check for HTTP errors
                if (!response.status.isSuccess()) {
                    console.error("HTTP error: ${response.status.value} ${response.status.description}")
                    val errorText = response.bodyAsText()
                    console.error("Error response:", errorText)
                    statusElement.innerHTML = "<p style='color: red; padding: 20px; text-align: center;'>An error occurred while processing your request. Please try again.</p>"
                    Analytics.apiError(INTELLIGENCE_CONTEXT, "conversation poll failed", response.status.value)
                    reportResponded(success = false)
                    break
                }

                val html = response.bodyAsText()

                // Update the status div with the server-rendered HTML
                statusElement.innerHTML = html

                // If the HTML doesn't contain "conversation-status", it's complete
                if (!html.contains("conversation-status")) {
                    reportResponded(success = true)
                    break
                }
            }
        } catch (e: Exception) {
            console.error("Error polling conversation:", e)
            statusElement.innerHTML = "<p style='color: red; padding: 20px; text-align: center;'>An error occurred. Please try again.</p>"
            Analytics.apiError(INTELLIGENCE_CONTEXT, "conversation polling threw")
            reportResponded(success = false)
        }
    }

    /**
     * Report that Intelligence finished. Paired one-to-one with `intelligence_queried`, so the
     * gap between the two counts is the share of questions that never got an answer — which is
     * the number worth watching for a feature that polls a model in the background.
     */
    private fun reportResponded(success: Boolean) {
        Analytics.track(
            AnalyticsEvent.INTELLIGENCE_RESPONDED,
            AnalyticsParam.SUCCESS to success
        )
    }

    /**
     * Renders an interactive button with specific styling and behavior tied to a click event.
     *
     * @param name The display name of the button.
     * @param onClick The action to be executed when the button is clicked.
     */
    private fun FlowContent.intelligenceButton(name: String, onClick: () -> Unit) {
        a(href = "#") {
            css {
                width = 100.pct
                display = Display.flex
                this.color = Color.white
                backgroundColor = VegasfulStyles.purple
                fontSize = 18.px
                fontWeight = FontWeight.w600
                padding = Padding(8.px)
                borderRadius = 5.px
                marginTop = 8.px

            }
            onClickFunction = {
                onClick()
            }
            span {
                css {
                    flexGrow = 1.0
                    textAlign = TextAlign.center
                }
                +name
            }
        }
    }
}
